import React, {useRef, useState} from 'react';

import {ReasonField, isValidReason} from 'src/components/save-with-reason/reason-field';
import {STATSIG_GATES} from 'src/contexts/statsig-contex/constants';

import Button, {CompoundButton} from '@pb/kit-component-button';
import {DialogFooter} from '@pb/kit-component-dialog-v2';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';
import {useGateValue} from '@pb/kit-lib-statsig';

import type {ActionOptions, ActionWithReason} from 'src/components/save-with-reason/action-with-reason-button';

type Props = {
    action: string,
    onConfirm: ActionWithReason,
    disabled?: boolean,
    busy?: boolean,
    onPendingChange?: (pending: boolean) => void,
    children?: React.ReactNode,
};

const FIELDSET_STYLE: React.CSSProperties = {border: 0, padding: 0, margin: 0, minWidth: 0};

export const BulkActionFooter = ({
    action, onConfirm, disabled = false, busy = false, onPendingChange, children,
}: Props) => {
    const enabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    const [withReason, setWithReason] = useState(false);
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string>();
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const pending = saving || busy;
    const reasonRequired = enabled && withReason;
    const actionDisabled = disabled || pending;

    const confirm = async (options?: ActionOptions) => {
        if (disabled || busy || savingRef.current) {
            return;
        }
        savingRef.current = true;
        onPendingChange?.(true);
        setSaving(true);
        setError(undefined);
        try {
            const result = await onConfirm(options);
            if (result) {
                setError(result);
            } else {
                setWithReason(false);
                setReason('');
            }
        } catch {
            setError('Unable to complete this action. Please try again.');
        } finally {
            savingRef.current = false;
            onPendingChange?.(false);
            setSaving(false);
        }
    };

    return (
        <DialogFooter style={{display: 'block'}}>
            <Group direction="v">
                {error ? <Message color="error">{error}</Message> : null}
                {reasonRequired ? <ReasonField reason={reason} onChange={setReason} pending={pending}/> : null}
                <fieldset disabled={pending} style={FIELDSET_STYLE}>
                    <Group>
                        {reasonRequired ? (
                            <>
                                <Button
                                    variant="Type.PRIMARY"
                                    busy={pending}
                                    disabled={actionDisabled || !isValidReason(reason)}
                                    onClick={() => confirm({reason: reason.trim()})}>
                                    {action}
                                </Button>
                                <Button variant="Type.SECONDARY" disabled={pending} onClick={() => setWithReason(false)}>
                                    Back
                                </Button>
                            </>
                        ) : null}
                        {!reasonRequired && enabled ? (
                            <CompoundButton
                                variant="Type.PRIMARY"
                                primaryButton={{
                                    children: `${action} with Reason...`,
                                    disabled: actionDisabled,
                                    busy: pending,
                                    onClick: () => {
                                        setWithReason(true);
                                        setError(undefined);
                                    },
                                }}
                                secondaryButton={{}}
                                renderSecondaryButton={props => (
                                    <DropdownMenu anchor={({getAnchorProps, anchorRef, positionRef}) => (
                                        <Button
                                            {...props}
                                            {...getAnchorProps()}
                                            ref={anchorRef}
                                            disabled={actionDisabled}
                                            aria-label={`More ${action.toLowerCase()} options`}
                                            startIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                        />
                                    )}>
                                        <DropdownMenuItem caption={`${action} without reason`} onClick={() => confirm()}/>
                                    </DropdownMenu>
                                )}
                            />
                        ) : null}
                        {!reasonRequired && !enabled ? (
                            <Button
                                variant="Type.PRIMARY"
                                disabled={actionDisabled}
                                busy={pending}
                                onClick={() => confirm()}>
                                {action}
                            </Button>
                        ) : null}
                        {children}
                    </Group>
                </fieldset>
            </Group>
        </DialogFooter>
    );
};
